import { Box, SxProps } from '@mui/material';
import { BenefitDTOBenefitEnum } from 'probonio-shared-ui/api';
import { CUSTOM_BENEFIT_ICONS } from 'probonio-shared-ui/component/icon';
import { apis, useTenantQuery } from 'probonio-shared-ui/module/api';
import { useTenant } from 'probonio-shared-ui/module/me';
import { useTranslation } from 'react-i18next';
import { useOrderedBenefitExtensions } from './useOrderedBenefits';

export interface BenefitRef {
  benefit: BenefitDTOBenefitEnum;
  customBenefitId?: string;
}

export const BenefitsCell: React.FC<{
  activeBenefits: BenefitRef[];
  upcomingBenefits?: BenefitRef[];
  inheritColor?: boolean;
  sx?: SxProps;
}> = ({ activeBenefits, upcomingBenefits, inheritColor, sx }) => {
  const { t } = useTranslation('benefitsModule');
  const { tenant } = useTenant();
  const { activeBenefits: benefits } = useOrderedBenefitExtensions(tenant);
  const { data: customBenefits, isFetched } = useTenantQuery(
    ['benefits', 'custom', apis.customBenefits.listCustomBenefits],
    tenantId =>
      apis.customBenefits
        .listCustomBenefits({
          tenantId,
        })
        .then(res => res.data),
    {
      gcTime: 60000,
      staleTime: 60000,
    },
  );

  if (!isFetched) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', width: '100%', columnGap: 0.2, flexWrap: 'wrap', ...sx }}>
      {benefits
        ?.filter(
          ({ benefitId }) =>
            benefitId !== BenefitDTOBenefitEnum.Custom && activeBenefits?.some(activeBenefit => activeBenefit.benefit === benefitId),
        )
        .map(benefitMetadata => {
          return (
            <benefitMetadata.Icon
              key={benefitMetadata.benefitId}
              color={inheritColor ? 'inherit' : 'primary'}
              titleAccess={t(`common:benefit.${benefitMetadata.benefitId}`)}
            />
          );
        })}
      {customBenefits?.results
        ?.filter(customBenefit => activeBenefits?.some(activeBenefit => activeBenefit.customBenefitId === customBenefit.id))
        .map(customBenefit => {
          const Icon = CUSTOM_BENEFIT_ICONS[customBenefit.iconName];
          return <Icon key={customBenefit.id} color={inheritColor ? 'inherit' : 'primary'} titleAccess={customBenefit.title} />;
        })}
      {benefits
        ?.filter(
          ({ benefitId }) =>
            benefitId !== BenefitDTOBenefitEnum.Custom && upcomingBenefits?.some(upcomingBenefit => upcomingBenefit.benefit === benefitId),
        )
        .map(benefitMetadata => {
          return (
            <benefitMetadata.Icon
              key={benefitMetadata.benefitId}
              color="primaryMuted"
              titleAccess={t(`common:benefit.${benefitMetadata.benefitId}`)}
            />
          );
        })}
      {customBenefits?.results
        ?.filter(customBenefit => upcomingBenefits?.some(upcomingBenefit => upcomingBenefit.customBenefitId === customBenefit.id))
        .map(customBenefit => {
          const Icon = CUSTOM_BENEFIT_ICONS[customBenefit.iconName];
          return <Icon key={customBenefit.id} color="primaryMuted" titleAccess={customBenefit.title} />;
        })}
    </Box>
  );
};
